import type { SourceSpec, SourceYield } from '../core/entities';
import type { GridPos } from '../core/types';

export type SourceId = 'sandDune' | 'ironRidge' | 'coalSeam' | 'copperMine';

interface NamedSource {
  id: SourceId;
  label: string;
  yields: readonly SourceYield[];
}

/** A source with ground under it: on the starting map, or arriving with an expansion. */
export interface PlacedSourceDef extends NamedSource {
  position: GridPos;
}

/** Designed in ROADMAP but behind no gate yet, so it can only be drawn — never built on. */
export interface PlannedSourceDef extends NamedSource {
  plannedReason: string;
}

export type SourceDef = PlacedSourceDef | PlannedSourceDef;

export const SAND_DUNE: PlacedSourceDef = {
  id: 'sandDune',
  label: 'Sand Dune',
  position: { x: 1, y: 2 },
  yields: [{ material: 'sand', weight: 5 }],
};

export const IRON_RIDGE: PlacedSourceDef = {
  id: 'ironRidge',
  label: 'Iron Ridge',
  position: { x: 9, y: 5 },
  yields: [
    { material: 'iron', weight: 4 },
    { material: 'slag', weight: 1 },
  ],
};

export const COAL_SEAM: PlacedSourceDef = {
  id: 'coalSeam',
  label: 'Coal Seam',
  position: { x: 11, y: 7 },
  yields: [{ material: 'carbon', weight: 5 }],
};

export const COPPER_MINE: PlacedSourceDef = {
  id: 'copperMine',
  label: 'Copper Mine',
  position: { x: 20, y: 12 },
  yields: [{ material: 'copper', weight: 5 }],
};

export const PLACED_SOURCES: readonly PlacedSourceDef[] = [
  SAND_DUNE,
  IRON_RIDGE,
  COAL_SEAM,
  COPPER_MINE,
];
export const PLANNED_SOURCES: readonly PlannedSourceDef[] = [];
export const SOURCE_DEFS: readonly SourceDef[] = [...PLACED_SOURCES, ...PLANNED_SOURCES];

export function isPlannedSource(source: SourceDef): source is PlannedSourceDef {
  return 'plannedReason' in source;
}

/**
 * Packets one source hands out per subscription before it needs an unsubscribe to refill. Sized
 * well above `from`'s fixed batch and any slot count `of` can afford, so it only ever bites the
 * one creation kind with no length of its own: `interval`.
 */
export const SOURCE_YIELD_CEILING = 24;

export function makeSource(
  position: GridPos,
  yields: readonly SourceYield[],
  capacity = SOURCE_YIELD_CEILING,
): SourceSpec {
  return {
    position: { ...position },
    yields: yields.map((yielded) => ({ ...yielded })),
    capacity,
    extracted: 0,
  };
}

export function sourceSpec(source: PlacedSourceDef): SourceSpec {
  return makeSource(source.position, source.yields);
}
